/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Shared visual metadata for agent types / statuses used by the redesigned
 * Agent Detail screen.
 */

export type AgentTypeId =
  | 'ASSISTANT'
  | 'AI_SYSTEM_BUILDER'
  | 'MAIN_ASSISTANT'
  | 'BOT_BUILDER'
  | 'MODEL_LAB_BUILDER';

export interface AgentTypeMeta {
  label: string;
  shortLabel: string;
  color: 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
}

export const AGENT_TYPE_META: Record<AgentTypeId, AgentTypeMeta> = {
  ASSISTANT: { label: 'Assistant', shortLabel: 'Assistant', color: 'primary' },
  AI_SYSTEM_BUILDER: { label: 'AI System Builder', shortLabel: 'AI Builder', color: 'secondary' },
  MAIN_ASSISTANT: { label: 'Main Assistant', shortLabel: 'Main', color: 'info' },
  BOT_BUILDER: { label: 'Bot Builder', shortLabel: 'Bot Builder', color: 'warning' },
  MODEL_LAB_BUILDER: { label: 'Model Lab Builder', shortLabel: 'Model Lab', color: 'success' },
};

export const getAgentTypeMeta = (type?: string | null): AgentTypeMeta =>
  AGENT_TYPE_META[(type || 'ASSISTANT') as AgentTypeId] || AGENT_TYPE_META.ASSISTANT;

export const getInitials = (name?: string | null): string => {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'A';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Deterministic hue from a string so each agent gets a stable accent colour.
 */
export const hashHue = (value: string): number => {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) {
    h = (h * 31 + value.charCodeAt(i)) >>> 0;
  }
  return h % 360;
};
